import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faPhoneAlt, faPlay, faHistory, faSignInAlt, faCopy, faKey } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import ProviderShell, { useProviderApi } from './layouts/ProviderShell';
import VideoComm from '../comms/VideoComm';

interface Appointment {
    id: number;
    title: string;
    appointment_type: 'in_person' | 'telehealth';
    status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
    start_datetime: string;
    end_datetime: string;
    patient: { patient_id: string; name: string };
}

interface MeetingType { id: number; typey: string; desc: string; }
interface MeetingCategory { id: number; category: string; description: string; }

interface Meeting {
    id: number;
    meet_title: string;
    meet_date: string;
    meet_time: string;
    access_mode: 'closed' | 'open';
    meet_auth: { meet_id: string; meet_url: string; meet_host: string };
    meet_type_detail: MeetingType;
}

const ProviderTelehealth: React.FC = () => {
    const { api } = useProviderApi();
    const [tab, setTab] = useState<'consult' | 'history'>('consult');

    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [loadingAppointments, setLoadingAppointments] = useState(true);
    const [types, setTypes] = useState<MeetingType[]>([]);
    const [categories, setCategories] = useState<MeetingCategory[]>([]);
    const [starting, setStarting] = useState<number | null>(null);

    const [joinCode, setJoinCode] = useState('');
    const [joinPasscode, setJoinPasscode] = useState('');
    const [joining, setJoining] = useState(false);

    const [past, setPast] = useState<Meeting[]>([]);
    const [loadingPast, setLoadingPast] = useState(false);

    const [activeCall, setActiveCall] = useState<{ meetId: string; passcode?: string; callType: 'audio' | 'video' } | null>(null);
    const [lastLink, setLastLink] = useState<{ url: string; passcode: string | null } | null>(null);

    useEffect(() => { document.title = 'Consultations & Telehealth - Uzima Healthcare'; }, []);

    const loadAppointments = async () => {
        setLoadingAppointments(true);
        try {
            const res = await api.get('practitioners/appointments/?appointment_type=telehealth');
            if (res.data?.status === 'success') {
                const upcoming = (res.data.data || []).filter((a: Appointment) => a.status === 'scheduled' || a.status === 'confirmed');
                setAppointments(upcoming);
            }
        } catch { setAppointments([]); } finally { setLoadingAppointments(false); }
    };

    const loadTypesAndCategories = async () => {
        try {
            const [typesRes, categoriesRes] = await Promise.all([
                api.get('comms/meeting-types'),
                api.get('comms/meeting-categories'),
            ]);
            if (typesRes.data?.status === 'success') setTypes(typesRes.data.data || []);
            if (categoriesRes.data?.status === 'success') setCategories(categoriesRes.data.data || []);
        } catch { /* start-consultation falls back gracefully if these are empty */ }
    };

    const loadPast = async () => {
        setLoadingPast(true);
        try {
            const res = await api.post('comms/past-meetings', {});
            if (res.data?.status === 'success') setPast(res.data.data?.meetings || []);
        } catch { setPast([]); } finally { setLoadingPast(false); }
    };

    useEffect(() => { loadAppointments(); loadTypesAndCategories(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (tab === 'history') loadPast(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    // create-meeting rejects a meet_time at or before "now" - a provider
    // clicking "Start Consultation" means right now, so the session is
    // scheduled 2 minutes out (passes validation) and then joined
    // immediately, rather than exposing a date/time picker for something
    // meant to start instantly.
    const startConsultation = async (appointment: Appointment) => {
        setStarting(appointment.id);
        try {
            const videoType = types.find(t => t.typey === 'video');
            const consultCategory = categories.find(c => c.category === 'Consultation') || categories[0];
            const startAt = new Date(Date.now() + 2 * 60 * 1000);
            const body = new URLSearchParams({
                meet_title: appointment.title,
                meet_desc: `Telehealth consultation with ${appointment.patient.name}`,
                meet_type: String(videoType?.id || 1),
                meet_categ: String(consultCategory?.id || 1),
                meet_date: startAt.toISOString().slice(0, 10),
                meet_time: startAt.toISOString().slice(11, 19),
                access_mode: 'closed',
            });
            const res = await api.post('comms/create-meeting', body);
            if (res.data?.status !== 'success') {
                throw new Error(res.data?.message || 'Could not start the consultation.');
            }
            const meeting = res.data.data;
            await api.patch(`practitioners/appointments/${appointment.id}/`, { status: 'confirmed' });
            setLastLink({ url: `${window.location.origin}${meeting.meet_auth.meet_url}`, passcode: meeting.meet_passcode });
            setActiveCall({ meetId: meeting.meet_auth.meet_id, passcode: meeting.meet_passcode || undefined, callType: 'video' });
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Could Not Start Call', text: err?.response?.data?.message || err.message });
        } finally {
            setStarting(null);
        }
    };

    const joinByCode = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!joinCode.trim()) return;
        setJoining(true);
        try {
            const body = new URLSearchParams({ meet_id: joinCode.trim(), passcode: joinPasscode.trim() });
            const res = await api.post('comms/join-meeting', body);
            if (res.data?.status !== 'success') {
                throw new Error(res.data?.message || 'Could not join meeting.');
            }
            setActiveCall({
                meetId: res.data.data.meet_id,
                passcode: joinPasscode.trim() || undefined,
                callType: res.data.data.call_type === 'audio' ? 'audio' : 'video',
            });
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Could Not Join', text: err?.response?.data?.message || err.message });
        } finally {
            setJoining(false);
        }
    };

    const copyLink = () => {
        if (!lastLink) return;
        navigator.clipboard.writeText(lastLink.passcode ? `${lastLink.url} (passcode: ${lastLink.passcode})` : lastLink.url);
        Swal.fire({ icon: 'success', title: 'Copied', timer: 1200, showConfirmButton: false });
    };

    if (activeCall) {
        return (
            <ProviderShell>
                <div className="mb-3">
                    <h5 className="fw-bold"><FontAwesomeIcon icon={faVideo} className="me-2 text-primary" />Live Consultation</h5>
                    {lastLink && (
                        <div className="alert alert-info d-flex justify-content-between align-items-center py-2">
                            <span>Share this with your patient: <code>{lastLink.url}</code>{lastLink.passcode && <> — passcode <strong>{lastLink.passcode}</strong></>}</span>
                            <button className="btn btn-sm btn-outline-primary" onClick={copyLink}><FontAwesomeIcon icon={faCopy} className="me-1" />Copy</button>
                        </div>
                    )}
                </div>
                <VideoComm
                    meetId={activeCall.meetId}
                    passcode={activeCall.passcode}
                    callType={activeCall.callType}
                    onEnd={() => { setActiveCall(null); setLastLink(null); loadAppointments(); }}
                />
            </ProviderShell>
        );
    }

    return (
        <ProviderShell>
            <div className="mb-4">
                <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faVideo} className="me-2 text-primary" />Consultations & Telehealth</h2>
                <p className="text-muted mb-0">Start or join secure video consultations with your patients</p>
            </div>

            <ul className="nav nav-pills mb-4">
                <li className="nav-item"><button className={`nav-link ${tab === 'consult' ? 'active' : ''}`} onClick={() => setTab('consult')}>Start / Join</button></li>
                <li className="nav-item"><button className={`nav-link ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}><FontAwesomeIcon icon={faHistory} className="me-1" />Call History</button></li>
            </ul>

            {tab === 'consult' && (
                <div className="row">
                    <div className="col-lg-7 mb-4">
                        <div className="card">
                            <div className="card-header"><strong>Upcoming Telehealth Appointments</strong></div>
                            <div className="card-body p-0">
                                {loadingAppointments ? (
                                    <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                                ) : appointments.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faVideo} className="text-muted mb-3" style={{ fontSize: '2.5rem' }} />
                                        <h5>No telehealth appointments scheduled</h5>
                                        <p className="text-muted">Schedule one under Appointments with type "Telehealth" first.</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead className="table-light"><tr><th>Patient</th><th>Title</th><th>When</th><th>Status</th><th></th></tr></thead>
                                            <tbody>
                                                {appointments.map(a => (
                                                    <tr key={a.id}>
                                                        <td>{a.patient.name}</td>
                                                        <td>{a.title}</td>
                                                        <td>{new Date(a.start_datetime).toLocaleString()}</td>
                                                        <td><span className={`badge bg-${a.status === 'confirmed' ? 'info' : 'warning'}`}>{a.status}</span></td>
                                                        <td className="text-end">
                                                            <button className="btn btn-sm btn-success" disabled={starting === a.id} onClick={() => startConsultation(a)}>
                                                                <FontAwesomeIcon icon={faPlay} className="me-1" />{starting === a.id ? 'Starting...' : 'Start'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-5 mb-4">
                        <div className="card">
                            <div className="card-header"><strong><FontAwesomeIcon icon={faSignInAlt} className="me-2" />Join a Consultation</strong></div>
                            <div className="card-body">
                                <p className="text-muted small">Have a meeting link or ID from a patient or colleague? Join it directly here.</p>
                                <form onSubmit={joinByCode}>
                                    <div className="mb-3">
                                        <label className="form-label">Meeting ID</label>
                                        <input className="form-control" required value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. 5a7fef2f-f1e2-4a29-b244-..." />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label"><FontAwesomeIcon icon={faKey} className="me-1" />Passcode (if required)</label>
                                        <input className="form-control" value={joinPasscode} onChange={(e) => setJoinPasscode(e.target.value)} />
                                    </div>
                                    <button className="btn btn-primary w-100" type="submit" disabled={joining}>
                                        <FontAwesomeIcon icon={faPhoneAlt} className="me-2" />{joining ? 'Joining...' : 'Join Consultation'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {tab === 'history' && (
                <div className="card">
                    <div className="card-header"><strong>Past Consultations</strong></div>
                    <div className="card-body p-0">
                        {loadingPast ? (
                            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                        ) : past.length === 0 ? (
                            <div className="text-center py-5 text-muted">No past consultations yet.</div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Title</th><th>Type</th><th>Date</th><th>Time</th></tr></thead>
                                    <tbody>
                                        {past.map(m => (
                                            <tr key={m.id}>
                                                <td>{m.meet_title}</td>
                                                <td>{m.meet_type_detail?.typey === 'audio' ? 'Audio' : 'Video'}</td>
                                                <td>{m.meet_date}</td>
                                                <td>{m.meet_time}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </ProviderShell>
    );
};

export default ProviderTelehealth;
